import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import wteLogo from '../assets/wte_logo.png';

const LAST_UPDATED = '1 August 2026';

const sections = [
  {
    title: '1. Who we are',
    body: [
      'This Privacy Policy describes how Water Treatment Equipment (WTE) handles information when you use the WTE Remote Data Monitoring System (RDMS / OMS) website and application (the “Service”).',
      'This Privacy Policy is provided for transparency regarding our information handling practices. It is not intended to constitute legal advice and does not, by itself, create a contractual relationship.',
    ],
  },
  {
    title: '2. Information we collect',
    body: [
      'Depending on how you use the Service, we may collect:',
    ],
    bullets: [
      'Account details you provide when you sign up or update your profile — such as your name, email address, company name, and authentication information necessary to access the Service. Passwords are securely managed by our authentication provider and are not stored by WTE.',
      'Usage and activity information related to your account — such as sign-in events and actions recorded in application activity logs.',
      'Operational and plant-related data that authorized users enter or that connected systems send to the Service — such as site information, instrument readings, and related monitoring data needed to run the OMS.',
      'Technical information automatically collected by the browser or hosting services — such as IP address, device/browser type, and basic request logs used for security and reliability.',
    ],
  },
  {
    title: '3. How we use information',
    body: [
      'We use the information above to:',
    ],
    bullets: [
      'Create and manage user accounts and access permissions.',
      'Provide monitoring, dashboards, alerts, and related OMS features.',
      'Support users, troubleshoot issues, and improve reliability and security.',
      'Communicate about account or service matters when needed.',
      
    ],
  },
  {
    title: '4. How we share information',
    body: [
      'We do not sell your personal information.',
      'Information may be shared only as needed to operate the Service, including:',
    ],
    bullets: [
      'With authorized users in your organization (for example, plant admins or operators with access to the same sites).',
      'With service providers that help us run the Service (for example, authentication, hosting, databases, and messaging tools we use to deliver the product).',
      'When we believe disclosure is required by law or to protect the security of the Service, our users, or others.',
    ],
  },
  {
    title: '5. Storage and security',
    body: [
      'Account authentication is handled through a secure authentication service. Application and operational data are stored on secure cloud infrastructure used to operate the Service.',
      'We limit access to authorized personnel and take reasonable steps to protect information. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.',
    ],
  },
  {
    title: '6. Data ownership',
    body: [
      'Operational data, monitoring data, reports, and project information entered into the Service remain the property of the respective customer or organization that provides the data. WTE processes this information solely for providing and supporting the Service.',
    ],
  },
  {
    title: '7. How long we keep information',
    body: [
      'We keep account and operational information for as long as needed to provide the Service, meet operational needs, resolve disputes, and comply with legal obligations that apply to us.',
      'If you request deletion of your account or related personal information, we will review the request and respond as reasonably possible. Some records may be retained where we are required or have a legitimate operational need to keep them (for example, security logs).',
    ],
  },
  {
    title: '8. Your choices',
    body: [
      'You can:',
    ],
    bullets: [
      'Update certain profile details from within the application where that feature is available.',
      'Contact us to ask what personal information we hold about you, or to request a correction or deletion.',
      'Stop using the Service and request account closure.',
    ],
  },
  {
    title: '9. Cookies and similar technologies',
    body: [
      'The Service may use cookies or similar technologies that are needed for basic functions such as keeping you signed in and remembering preferences (for example, theme settings stored in your browser).',
      'We do not use the Service to run third-party advertising trackers.',
    ],
  },
  {
    title: '10. Children’s privacy',
    body: [
      'The Service is intended for business and professional use. It is not directed at children, and we do not knowingly collect personal information from children.',
    ],
  },
  {
    title: '11. Changes to this page',
    body: [
      'We may update this Privacy Policy from time to time. When we do, we will change the “Last updated” date at the top of this page. Continued use of the Service after an update means you should review the revised policy.',
    ],
  },
  {
    title: '12. Contact us',
    body: [
      'If you have questions about this Privacy Policy or about personal information related to the Service, contact us through our website:',
    ],
  },
];

const CLAUDE_LIKE_FONT =
  '"Source Sans 3", "Segoe UI", system-ui, -apple-system, sans-serif';

export default function PrivacyPolicyPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  const readingText = {
    fontFamily: CLAUDE_LIKE_FONT,
    fontSize: { xs: '1.05rem', md: '1.125rem' },
    lineHeight: 1.7,
    letterSpacing: '0.01em',
    fontWeight: 400,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: isDark ? 'rgba(15, 20, 36, 0.98)' : 'rgba(245, 249, 255, 0.98)',
        pb: 8,
        fontFamily: CLAUDE_LIKE_FONT,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      }}
    >
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: isDark ? alpha('#0d47a1', 0.25) : alpha('#1976d2', 0.06),
          py: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="md">
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                component="img"
                src={wteLogo}
                alt="WTE"
                sx={{ height: 40, cursor: 'pointer' }}
                onClick={() => navigate('/')}
              />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: CLAUDE_LIKE_FONT,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                }}
              >
                Privacy Policy
              </Typography>
            </Stack>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ textTransform: 'none', fontFamily: CLAUDE_LIKE_FONT }}
            >
              Back
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pt: { xs: 4, md: 6 } }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ...readingText, fontSize: '0.95rem', mb: 1 }}
        >
          Last updated: {LAST_UPDATED}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            ...readingText,
            mb: 4,
            p: 2.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#0d47a1', 0.03),
            color: 'text.secondary',
          }}
        >
          This notice explains what information the WTE OMS / RDMS product
          collects and how we use it. It is written for transparency on a professional website and
          is not a substitute for legal advice.
        </Typography>

        {sections.map((section) => (
          <Box key={section.title} sx={{ mb: 4.5 }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontFamily: CLAUDE_LIKE_FONT,
                fontWeight: 600,
                fontSize: { xs: '1.2rem', md: '1.3rem' },
                letterSpacing: '-0.02em',
                mb: 1.5,
                color: isDark ? 'primary.light' : 'primary.dark',
              }}
            >
              {section.title}
            </Typography>
            {section.body.map((paragraph) => (
              <Typography
                key={paragraph}
                variant="body1"
                color="text.secondary"
                sx={{ ...readingText, mb: 1.5 }}
              >
                {paragraph}
              </Typography>
            ))}
            {section.bullets && (
              <Box component="ul" sx={{ m: 0, pl: 3, color: 'text.secondary' }}>
                {section.bullets.map((item) => (
                  <Typography
                    key={item}
                    component="li"
                    variant="body1"
                    sx={{ ...readingText, mb: 1.25 }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            )}
            {section.title === '12. Contact us' && (
              <Typography variant="body1" sx={{ ...readingText, mt: 1 }}>
                <Box
                  component="a"
                  href="https://wteinfra.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    textDecoration: 'none',
                    fontFamily: CLAUDE_LIKE_FONT,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  https://wteinfra.com/contact
                </Box>
              </Typography>
            )}
          </Box>
        ))}

        <Divider sx={{ my: 4 }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: CLAUDE_LIKE_FONT }}
          >
            © {new Date().getFullYear()} WTE Infra Projects Pvt Ltd. All rights reserved.
          </Typography>
          <Typography
            component={RouterLink}
            to="/"
            variant="body2"
            sx={{
              fontFamily: CLAUDE_LIKE_FONT,
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Back to Home
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
